import {
  Box,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { type FC, useMemo } from "react"
import ConditionalRender from "@/common/components/ConditionalRender"
import CopyButton from "@/common/components/CopyButton"
import { EXTERNAL_ROUTES } from "@/common/constants/routes"
import type { RewardRecipient } from "@/createProposal/action/TransferArbitrumErc20Action"
import { recipeReadableMap } from "@/createProposal/action/types"
import type { CreateProposalFormValues } from "@/createProposal/types/form"
import ReportSection from "@/proposal/components/simulation/common/ReportSection"
import SimulationLink from "@/proposal/components/simulation/common/SimulationLink"
import type { SimulationResultType } from "@/proposal/components/simulation/common/SimulationResultBadge"
import { mapStatusToSimulationStatus } from "@/proposal/components/simulation/helpers/simulation"
import type { ProposalExecutableCall } from "@/proposal/components/simulation/ProposalImpactOverview"
import type { TransactionSimulationV2 } from "@/query/graphql"
import { ExecutableCallType } from "@/query/graphql"
import { shortenIfAddress } from "@/web3/helpers/address"

type Action = {
  recipe?: ExecutableCallType
  rewardsRecipients?: RewardRecipient[]
  sellToken?: { symbol: string }
}

type MappedSimulation = {
  action: number
  type?: ExecutableCallType
  simulation: TransactionSimulationV2
}

function mapSimulationsToActions(
  actions: Action[],
  simulations: TransactionSimulationV2[] | null | undefined,
): MappedSimulation[] | undefined {
  if (!simulations) return undefined

  const fullAccumulatedArray = actions.reduce(
    (acc: MappedSimulation[], action, actionIndex) => {
      const makeBaseObject = (index: number) => ({
        action: actionIndex,
        type: action.recipe,
        simulation: simulations[index],
      })

      switch (action.recipe) {
        case ExecutableCallType.Custom:
          return [...acc, makeBaseObject(actionIndex)]

        case ExecutableCallType.Swap:
          const objectsForSwap = action.sellToken?.symbol === "ETH" ? 4 : 3

          return [
            ...acc,
            ...Array.from({ length: objectsForSwap }, (_) =>
              makeBaseObject(actionIndex),
            ),
          ]

        case ExecutableCallType.Nativetransfer:
        case ExecutableCallType.Erc20transfer:
        case ExecutableCallType.Erc20transferarbitrum:
          const additionalObjectsCount =
            action.rewardsRecipients && action.rewardsRecipients.length > 0
              ? action.rewardsRecipients.length
              : 0

          return [
            ...acc,
            ...Array.from({ length: 1 + additionalObjectsCount }, (_) =>
              makeBaseObject(actionIndex),
            ),
          ]

        default:
          return [...acc, makeBaseObject(0)]
      }
    },
    [],
  )

  const accumulatedArray = fullAccumulatedArray.reduce<MappedSimulation[]>(
    (acc, current) => {
      const isDuplicate = acc.some((item) => item.action === current.action)

      if (!isDuplicate) {
        acc.push(current)
      }

      return acc
    },
    [],
  )

  if (accumulatedArray.length !== simulations.length) return undefined

  return accumulatedArray
}

enum TableType {
  MappedSimulations = "mappedSimulations",
  ExecutableCalls = "executableCalls",
  General = "general",
}

type Props = {
  simulations: TransactionSimulationV2[] | null
  result?: SimulationResultType
  executableCalls?: ProposalExecutableCall[]
  values?: CreateProposalFormValues
}
const TenderlyReportSection: FC<Props> = ({
  simulations,
  executableCalls,
  values,
  result,
}) => {
  const mappedSimulations = useMemo(() => {
    if (values?.actions) {
      return mapSimulationsToActions(values.actions, simulations)
    }
  }, [values?.actions, simulations])

  const tableType: TableType = useMemo(() => {
    if (mappedSimulations && mappedSimulations.length > 0) {
      return TableType.MappedSimulations
    }

    if (executableCalls && executableCalls.length > 0) {
      return TableType.ExecutableCalls
    }

    return TableType.General
  }, [mappedSimulations, executableCalls])

  return (
    <ReportSection
      learnMoreLink={{
        url: EXTERNAL_ROUTES.simulations.tenderly(),
        label: "Learn more about Tenderly",
      }}
      result={result}
      title="Tenderly simulations"
    >
      <TableContainer
        background="white"
        border="0px"
        borderColor="gray.200"
        mb={1}
        width="full"
      >
        <ConditionalRender
          condition={tableType === TableType.MappedSimulations}
        >
          <MappedSimulationsTable mappedSimulations={mappedSimulations ?? []} />
        </ConditionalRender>

        <ConditionalRender condition={tableType === TableType.ExecutableCalls}>
          <ExecutableCallsTable
            executableCalls={executableCalls ?? []}
            simulations={simulations ?? []}
          />
        </ConditionalRender>

        <ConditionalRender condition={tableType === TableType.General}>
          <GeneralTable simulations={simulations ?? []} />
        </ConditionalRender>
      </TableContainer>
    </ReportSection>
  )
}

type MappedSimulationsTableProps = {
  mappedSimulations: MappedSimulation[]
}
const MappedSimulationsTable: FC<MappedSimulationsTableProps> = ({
  mappedSimulations,
}) => {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th px={3} py={3}>
            #
          </Th>
          <Th px={3} py={3}>
            Type
          </Th>
          <Th isNumeric pr={10} py={3}>
            Simulation
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {mappedSimulations.map((mappedSimulation, idx) => {
          return (
            <Tr key={`simulation-${idx}`}>
              <Td px={3} py={3}>
                <Text>{mappedSimulation.action + 1}</Text>
              </Td>
              <Td px={3} py={3}>
                <Text>
                  {mappedSimulation.type
                    ? recipeReadableMap[mappedSimulation.type]
                    : ""}
                </Text>
              </Td>
              <Td isNumeric px={3} py={3}>
                {mappedSimulation.simulation ? (
                  <SimulationLink
                    result={mapStatusToSimulationStatus(
                      mappedSimulation.simulation.result,
                    )}
                    url={mappedSimulation.simulation.publicURI}
                  />
                ) : null}
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

type GeneralTableProps = {
  simulations: TransactionSimulationV2[]
}
const GeneralTable: FC<GeneralTableProps> = ({ simulations }) => {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th px={3} py={3}>
            #
          </Th>
          <Th isNumeric pr={10} py={3}>
            Simulation
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {simulations?.map((simulation, idx) => {
          return (
            <Tr key={`simulation-${idx}`}>
              <Td px={3} py={3}>
                <Text>Action {idx + 1}</Text>
              </Td>
              <Td isNumeric px={3} py={3}>
                <SimulationLink
                  result={mapStatusToSimulationStatus(simulation.result)}
                  url={simulation.publicURI}
                />
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

type ExecutableCallsTableProps = {
  executableCalls: ProposalExecutableCall[]
  simulations: TransactionSimulationV2[]
}
const ExecutableCallsTable: FC<ExecutableCallsTableProps> = ({
  executableCalls,
  simulations,
}) => {
  if (simulations.length === 0) {
    return (
      <Box p={3}>
        <Text>Report not available</Text>
      </Box>
    )
  }

  return (
    <Table>
      <Thead>
        <Tr>
          <Th maxWidth="10%" minWidth="10%" px={3} py={3} whiteSpace="nowrap">
            #
          </Th>
          <Th maxWidth="30%" minWidth="30%" px={3} py={3}>
            Contract
          </Th>
          <Th maxWidth="30%" minWidth="30%" px={3} py={3}>
            Function
          </Th>
          <Th isNumeric maxWidth="30%" minWidth="30%" pr={10} py={3}>
            Simulation
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {simulations?.map((simulation, idx) => {
          const executableCall = executableCalls?.[idx]

          return (
            <Tr key={`simulation-${idx}`}>
              <Td
                maxWidth="10%"
                minWidth="10%"
                px={3}
                py={3}
                whiteSpace="nowrap"
              >
                <Text fontSize="sm">{idx + 1}</Text>
              </Td>
              <Td maxWidth="30%" minWidth="30%" px={3} py={3}>
                <Text fontSize="sm">
                  {executableCall.target ? (
                    <Stack align="center" direction="row">
                      <Text>{shortenIfAddress(executableCall.target)}</Text>
                      <CopyButton
                        size={8}
                        tooltipLabel="Target contract address"
                        value={executableCall.target}
                      />
                    </Stack>
                  ) : (
                    "-"
                  )}
                </Text>
              </Td>
              <Td maxWidth="30%" minWidth="30%" px={3} py={3}>
                <Text fontSize="sm">{executableCall.signature ?? "-"}</Text>
              </Td>
              <Td isNumeric maxWidth="30%" minWidth="30%" px={3} py={3}>
                <SimulationLink
                  result={mapStatusToSimulationStatus(simulation.result)}
                  url={simulation.publicURI}
                />
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

export default TenderlyReportSection
